import { useEffect, useMemo, useRef } from "react";

import { buildComputationGraph } from "../engine/computation-graph.js";
import { buildItemLookups, runComputations } from "../engine/computation-pass.js";
import { isGroupGated, isHiddenAlways } from "../engine/form-visibility.js";
import { useTypeMismatchReporting } from "./useTypeMismatchReporting.js";

// Reactive recompute debounce, so fast keystrokes don't cause
// thundering-herd evaluations.
const RECOMPUTE_DEBOUNCE_MS = 300;

/**
 * Merges work owed to a pending debounce timer with newly scheduled work.
 * `null` means "every computed item" and supersedes any scoped Set; two Sets
 * union; an `undefined` pending means nothing was owed.
 *
 * @param {Set<string> | null | undefined} pending
 * @param {Set<string> | null} next
 * @returns {Set<string> | null}
 */
function mergeAffectedOids(pending, next) {
    if (next === null || pending === null) return null;
    return pending ? new Set([...pending, ...next]) : next;
}

/**
 * Shallow equality check by key/value identity. `null`/`undefined`/missing
 * are all treated as "empty object" so initial-load shapes don't flag a
 * spurious change.
 *
 * @param {object | null | undefined} a
 * @param {object | null | undefined} b
 * @returns {boolean}
 */
function shallowEqual(a, b) {
    if (a === b) return true;
    const aObj = a ?? {};
    const bObj = b ?? {};
    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
        if (aObj[key] !== bObj[key]) return false;
    }
    return true;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Applies `MethodDef` Computation functions on first form load and whenever a
 * referenced item value changes.
 *
 * - Builds a dependency graph and topological order at definition load time
 *   (memoised on `form.itemGroups` identity).
 * - Initial compute fires once after `useDefaults` has populated Imputation
 *   defaults (both hooks react to `form` changing; since `useDefaults`
 *   dispatches SET_ITEM_VALUE synchronously via the reducer, `form.formData`
 *   will already contain the default values by the time this effect runs).
 * - Reactive recompute is debounced ~300 ms so that fast keystrokes don't
 *   cause thundering-herd evaluations.
 * - Conditionally-hidden items are skipped; hide-always items (see
 *   isHiddenAlways) compute in the background so the value reaches the submit
 *   payload. Items in a gated group are skipped too (see isGroupGated) and
 *   recomputed once the group stops being gated. Likewise a conditionally
 *   hidden item is recomputed when its visibility condition stops hiding it
 *   (updateVisibility cleared its value while hidden, and the item that
 *   flipped the condition is typically not referenced by the computation's
 *   expression).
 * - Cycles are detected; offending OIDs are console.warn'd and excluded from
 *   recompute.
 * - Uses `evaluateExpressionSafe` so a bad expression never freezes the UI.
 * - When `readOnly` is true (submitted/scheduled/missing form) no computation
 *   runs at all: the form must display the values stored at submission time,
 *   not values re-derived from dependencies that may have changed since.
 *
 * - A result whose type the item cannot render (the classic case being `today`
 *   written without its `()`, which yields the function itself) is NOT stored:
 *   the item is cleared to its empty value and the mismatch is reported. Left
 *   unchecked such a value looks like it worked — React invokes it as a
 *   `useState` lazy initializer so the field displays a plausible date, while
 *   formData keeps the function and `JSON.stringify` drops it from the submitted
 *   payload. See computed-type-check.js for the measured detail.
 *
 * @param {{
 *   form: {
 *     itemGroups: Array<{ oid: string, isVisible: boolean, items: Array }>,
 *     formData: object,
 *     dependencies: object,
 *     identifierModel: object,
 *   },
 *   setValue: (oid: string, value: *) => void,
 *   setItemValue: (oid: string, value: *, format?: *) => void,
 *   readOnly: boolean,
 *   onTypeMismatch?: (mismatch:
 *     | { oid: string, label?: string, itemType?: string,
 *         expression: string, expected: string, actual: string, message: string }
 *     // Sent when an item previously reported now computes an assignable
 *     // value, or stops being evaluated (hidden / gated group). Carries the
 *     // oid only — the caller keys its UI by oid and should drop that entry.
 *     // Only ever sent for an oid previously reported.
 *     | { oid: string, resolved: true }
 *   ) => void,
 * }} params
 */
export function useFunctionRecompute({
    form,
    setValue,
    setItemValue,
    readOnly,
    onTypeMismatch,
}) {
    const { reportTypeMismatch, resolveTypeMismatch, resetTypeMismatches } =
        useTypeMismatchReporting(onTypeMismatch);

    // Keep a ref to the latest form snapshot so debounce callbacks always
    // read current data even if the component re-rendered between schedule
    // and fire.
    const latestFormRef = useRef(form);
    latestFormRef.current = form;
    // Same for readOnly: a timer armed on an editable form can fire after a
    // swap to a read-only one has rendered but before the reactive effect has
    // cancelled it (timers and passive effects are separate tasks), and a
    // recompute against the read-only form would clear stored submitted values.
    const latestReadOnlyRef = useRef(readOnly);
    latestReadOnlyRef.current = readOnly;

    const { computedItems, topoOrder, computedItemMap, computedOidsByGroup } =
        useMemo(
            () => buildComputationGraph(form?.itemGroups ?? []),
            [form?.itemGroups]
        );

    // --------------------------------------------------------------------------
    // Build lookups: oid → item object (for isVisible) and oid → its group (for
    // the gate). Both hold the live nodes, which updateVisibility mutates in
    // place, so isVisible is always current.
    // --------------------------------------------------------------------------
    const { itemLookup, groupLookup } = useMemo(
        () => buildItemLookups(form?.itemGroups),
        [form?.itemGroups]
    );

    // The pass itself lives in the engine. It is handed exactly what the
    // closure used to read, and writes through the same callbacks, so the
    // operations and their order are what this function performed inline.
    const runPass = ({ formData, dependencies, identifierModel, affectedOids }) =>
        runComputations({
            form,
            computedItems,
            topoOrder,
            computedItemMap,
            itemLookup,
            groupLookup,
            formData,
            dependencies,
            identifierModel,
            affectedOids,
            setValue,
            setItemValue,
            reportTypeMismatch,
            resolveTypeMismatch,
        });

    // --------------------------------------------------------------------------
    // Initial compute — fires after form loads (and after useDefaults runs,
    // because both effects depend on `form` and React flushes them in order).
    // --------------------------------------------------------------------------
    const initialDoneRef = useRef(false);
    // Track the itemGroups identity that last triggered an initial-compute so
    // we can detect a SET_FORM-driven definition swap (re-fetch on save, on
    // language switch, etc.) and re-run computations on the new model. Without
    // this, zero-dependency Computation items (e.g. `now()`) never re-evaluate
    // after the first form load because the reactive path needs a referenced
    // OID to change.
    const initialItemGroupsRef = useRef(null);

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally depends on full form object to re-fire on definition reload; runComputations is a local fn
    useEffect(() => {
        // Re-arm the guard whenever the itemGroups identity changes — that's
        // the signal a SET_FORM happened (new definition loaded, language
        // switch, re-fetch after save). Without re-arming, zero-dependency
        // Computation items (e.g. `now()`) never re-evaluate after the first
        // form load because the reactive path needs a referenced OID to
        // change.
        if (form.itemGroups !== initialItemGroupsRef.current) {
            initialDoneRef.current = false;
            initialItemGroupsRef.current = form.itemGroups;
            // A new definition means new expressions — see the reset's doc.
            resetTypeMismatches();
        }

        // A read-only form shows the stored values as-is — evaluating the
        // expressions against current dependencies could overwrite the value
        // that was preserved at submission time.
        if (readOnly) return;

        if (!form?.itemGroups?.length || initialDoneRef.current) return;
        initialDoneRef.current = true;

        runPass({
            formData: form.formData,
            dependencies: form.dependencies,
            identifierModel: form.identifierModel,
        });
    }, [form, readOnly]);

    // --------------------------------------------------------------------------
    // Reactive recompute — debounced 300 ms
    // --------------------------------------------------------------------------
    const prevFormDataRef = useRef(null);
    const prevDependenciesRef = useRef(null);
    const prevIdentifierModelRef = useRef(null);
    // Hidden computed OIDs from the previous pass: a Computation item whose
    // own visibility condition stops hiding it needs a recompute (its value
    // was cleared by updateVisibility and runComputations skipped it while
    // hidden), but the item that triggered the flip is not referenced by the
    // computation's expression, so the formData diff alone never schedules it.
    const prevHiddenComputedRef = useRef(null);
    const debounceTimerRef = useRef(null);
    // Gated groups from the previous pass: a group that stops being gated needs
    // its computed items recomputed even though none of their own inputs moved.
    const prevGatedGroupsRef = useRef(null);
    // Work promised to a pending debounce timer: a Set of OIDs, or null for
    // "every computed item". Re-arming merges into it so cancelling the timer
    // cannot drop a one-shot signal (a group un-gating, a dependencies change).
    const pendingAffectedRef = useRef(undefined);
    // itemGroups identity last seen by the reactive effect, to detect a
    // SET_FORM definition swap and start the diffing from a fresh snapshot.
    const reactiveItemGroupsRef = useRef(null);

    /** Drops any armed timer and the work promised to it. */
    const cancelScheduledRecompute = () => {
        pendingAffectedRef.current = undefined;
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    };

    /**
     * (Re-)arms the debounced reactive pass. `affectedOids` is a Set, or null
     * to run every computed item; any work a cancelled timer still owed is
     * merged in (see mergeAffectedOids).
     */
    const scheduleRecompute = (affectedOids) => {
        // Capture the definition whose graph is scheduling this work. A route
        // can render another editable form before the passive effect gets a
        // chance to cancel this timer; in that window latestFormRef is already
        // new while runComputations/topoOrder still belong to the old graph.
        const scheduledItemGroups = latestFormRef.current?.itemGroups;
        const merged = mergeAffectedOids(
            pendingAffectedRef.current,
            affectedOids
        );
        pendingAffectedRef.current = merged;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            pendingAffectedRef.current = undefined;
            // See latestReadOnlyRef: the form may have turned read-only
            // between schedule and fire.
            if (latestReadOnlyRef.current) return;
            // Read from the ref to get the latest form — avoids the
            // stale-closure bug where `form` captured at schedule time points
            // to a prior render.
            const currentForm = latestFormRef.current;
            if (currentForm?.itemGroups !== scheduledItemGroups) return;
            runPass({
                formData: currentForm.formData,
                dependencies: currentForm.dependencies,
                identifierModel: currentForm.identifierModel,
                affectedOids: merged ?? undefined,
            });
        }, RECOMPUTE_DEBOUNCE_MS);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: tracks formData / dependencies / identifierModel changes; all other deps (computedItems, topoOrder, etc.) are stable within a form definition. dependencies + identifierModel are needed so /preview sidebar edits trigger recompute — see docs/features/2-preview-route.md.
    useEffect(() => {
        // A definition swap (SET_FORM) invalidates every snapshot and any
        // pending timer: diffing data or gate state across two definitions
        // would schedule reactive passes for changes no participant made. The
        // initial-compute effect owns the new definition's first evaluation.
        // Runs before the readOnly gate (like the initial-compute effect):
        // the route reuses this mounted component across events, so a swap to
        // a read-only form must still cancel a timer armed while the previous
        // form was editable — left pending, it would fire into the read-only
        // form and clear stored submitted values.
        if (form?.itemGroups !== reactiveItemGroupsRef.current) {
            reactiveItemGroupsRef.current = form?.itemGroups;
            prevFormDataRef.current = null;
            prevDependenciesRef.current = null;
            prevIdentifierModelRef.current = null;
            prevHiddenComputedRef.current = null;
            prevGatedGroupsRef.current = null;
            cancelScheduledRecompute();
        }

        // Read-only forms never recompute — see the initial-compute guard.
        if (readOnly) return;

        // Skip reactive recompute until the form is loaded
        if (!form?.itemGroups?.length || !computedItems.length) {
            prevFormDataRef.current = form?.formData ?? null;
            prevDependenciesRef.current = form?.dependencies ?? null;
            prevIdentifierModelRef.current = form?.identifierModel ?? null;
            return;
        }

        const currentFormData = form.formData ?? {};
        const currentDependencies = form.dependencies ?? {};
        const currentIdentifierModel = form.identifierModel ?? {};

        // itemLookup holds the live item nodes, which updateVisibility mutates
        // in place, so isVisible is always current by the time this effect
        // runs. Mirrors runComputations' skip: falsy isVisible, except
        // hide-always items, which compute in the background and so have
        // nothing to recompute on a visibility flip.
        const hiddenComputedOids = new Set();
        for (const oid of computedItemMap.keys()) {
            const itemMeta = itemLookup.get(oid);
            if (!itemMeta?.isVisible && !isHiddenAlways(itemMeta)) {
                hiddenComputedOids.add(oid);
            }
        }

        const gatedGroups = new Set(
            form.itemGroups.filter((ig) => isGroupGated(ig)).map((ig) => ig.oid)
        );

        // On the very first run after form load, just snapshot and return.
        // The initial compute effect owns the first evaluation; we don't want
        // to also schedule a debounced recompute from the initial diff.
        if (prevFormDataRef.current === null) {
            prevFormDataRef.current = currentFormData;
            prevDependenciesRef.current = currentDependencies;
            prevIdentifierModelRef.current = currentIdentifierModel;
            prevHiddenComputedRef.current = hiddenComputedOids;
            prevGatedGroupsRef.current = gatedGroups;
            return;
        }

        const prevFormData = prevFormDataRef.current;
        // Shallow content comparison (NOT identity) — the live form path
        // produces fresh `{}` objects on every render even when content
        // hasn't moved, which would otherwise cause spurious recomputes.
        const dependenciesChanged = !shallowEqual(
            prevDependenciesRef.current,
            currentDependencies
        );
        const identifierModelChanged = !shallowEqual(
            prevIdentifierModelRef.current,
            currentIdentifierModel
        );

        // Diff: find OIDs whose values changed since last tick
        const changedOids = new Set();
        const allKeys = new Set([
            ...Object.keys(currentFormData),
            ...Object.keys(prevFormData),
        ]);
        for (const key of allKeys) {
            if (currentFormData[key] !== prevFormData[key]) {
                changedOids.add(key);
            }
        }

        // Update snapshots
        const prevHiddenComputed = prevHiddenComputedRef.current ?? hiddenComputedOids;
        const prevGatedGroups = prevGatedGroupsRef.current ?? gatedGroups;
        prevFormDataRef.current = currentFormData;
        prevDependenciesRef.current = currentDependencies;
        prevIdentifierModelRef.current = currentIdentifierModel;
        prevHiddenComputedRef.current = hiddenComputedOids;
        prevGatedGroupsRef.current = gatedGroups;

        // A computed item the passes now skip (hidden by a regular condition,
        // or in a gated group) is no longer evaluated and updateVisibility has
        // cleared its value, so a mismatch reported while it was evaluated
        // must not keep a panel row standing against an empty, hidden field
        // (see resolveTypeMismatch). Resolved here rather than at the skips in
        // runComputations, because a reactive pass only visits affected OIDs
        // and the item that was just hidden need not be one of them.
        for (const oid of computedItemMap.keys()) {
            const itemMeta = itemLookup.get(oid);
            if (
                (!itemMeta?.isVisible && !isHiddenAlways(itemMeta)) ||
                isGroupGated(groupLookup.get(oid))
            ) {
                resolveTypeMismatch(oid);
            }
        }

        // Items that were hidden and no longer are: their value was cleared
        // while hidden, so recompute them now.
        const unhiddenOids = new Set();
        for (const oid of prevHiddenComputed) {
            if (!hiddenComputedOids.has(oid)) {
                unhiddenOids.add(oid);
            }
        }

        // Groups that were gated and no longer are: their computed items were
        // cleared and skipped while gated, so recompute them now.
        const ungatedOids = new Set();
        for (const groupOid of prevGatedGroups) {
            if (gatedGroups.has(groupOid)) {
                continue;
            }
            for (const oid of computedOidsByGroup.get(groupOid) ?? []) {
                ungatedOids.add(oid);
            }
        }

        // If dependencies or identifierModel changed (typically a /preview
        // sidebar edit), we can't tell from the in-form diff which Computation
        // items reference them. Re-run the full computed-item set so any
        // expression touching the changed cross-form value picks it up.
        if (dependenciesChanged || identifierModelChanged) {
            // No affectedOids filter: which items reference the changed
            // cross-form value is unknown, so every computed item runs.
            scheduleRecompute(null);
            return;
        }

        if (
            changedOids.size === 0 &&
            unhiddenOids.size === 0 &&
            ungatedOids.size === 0
        ) {
            return;
        }

        // Determine which Computation items are affected (direct or transitive)
        // Walk the topo order: an item is affected if any of its dependsOn OIDs
        // are in changedOids (which grows as we process earlier computed items).
        // Seeded with the unhidden and un-gated items so they recompute even
        // though none of their referenced OIDs changed, and so their
        // dependents follow.
        const affectedOids = new Set([...unhiddenOids, ...ungatedOids]);
        for (const oid of topoOrder) {
            const item = computedItemMap.get(oid);
            if (!item) continue;
            const isDirectlyAffected = [...item.dependsOn].some((dep) =>
                changedOids.has(dep)
            );
            const isTransitivelyAffected = [...item.dependsOn].some((dep) =>
                affectedOids.has(dep)
            );
            if (isDirectlyAffected || isTransitivelyAffected) {
                affectedOids.add(oid);
                // Treat the computed OID itself as "changed" for downstream items
                changedOids.add(oid);
            }
        }

        if (affectedOids.size === 0) return;

        scheduleRecompute(affectedOids);
    }, [form?.formData, form?.dependencies, form?.identifierModel]);

    // Clear debounce timer on unmount
    // biome-ignore lint/correctness/useExhaustiveDependencies: cancelScheduledRecompute only touches refs; unmount-only by design
    useEffect(() => {
        return () => {
            cancelScheduledRecompute();
        };
    }, []);
}
